import { useCallback, useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMesterplanApi } from '@mesterplan/web-hooks';
import type {
  AnnouncementDto,
  AnnouncementFeedDto,
  AnnouncementFeedQuery,
  ApiResult,
  MesterplanApi,
} from '@mesterplan/api-client';
import { useNetUser } from '@/hooks/useNetUser';
import { WEB_BUILD_DATE } from '@/lib/clientVersion';

/**
 * "Nyt i Mesterplan" data layer.
 *
 * Announcements are product-wide and read state / the opt-out are per user, so the cache key is the
 * user only; switching the active org must not refetch or lose the badge.
 */
export const ANNOUNCEMENTS_QUERY_KEY = 'announcements';

export function announcementFeedQueryKey(userId: string | null | undefined): readonly [string, string | null] {
  return [ANNOUNCEMENTS_QUERY_KEY, userId ?? null] as const;
}

/** Thrown by the query and the mutations so callers can branch on the HTTP status. */
export class AnnouncementsError extends Error {
  constructor(
    readonly status: number,
    detail?: string,
  ) {
    super(detail ?? `Announcements request failed (${status})`);
    this.name = 'AnnouncementsError';
  }
}

function unwrap<T>(res: ApiResult<T>): T {
  if (!res.ok) throw new AnnouncementsError(res.status, res.error?.detail);
  return res.data;
}

/** The gate sent with both the feed and markRead, so a read only touches notes this build can see. */
function webAnnouncementQuery(): AnnouncementFeedQuery {
  return { platform: 'web', ...(WEB_BUILD_DATE ? { build: WEB_BUILD_DATE } : {}) };
}

const EMPTY_ITEMS: AnnouncementDto[] = [];

/** Pure: the feed after `ids` (or everything, when omitted) were read. Exported for tests. */
export function applyRead(feed: AnnouncementFeedDto, ids: string[] | undefined, unreadCount?: number): AnnouncementFeedDto {
  const target = ids && ids.length > 0 ? new Set(ids) : null;
  const items = feed.items.map((item) => (!target || target.has(item.id) ? { ...item, isRead: true } : item));
  return { ...feed, items, unreadCount: unreadCount ?? items.filter((item) => !item.isRead).length };
}

export interface AnnouncementFeed {
  items: AnnouncementDto[];
  unreadItems: AnnouncementDto[];
  /** 0 while loading and on error: a failed feed reads as "nothing to show". */
  unreadCount: number;
  /** `undefined` until the feed has arrived. That must read as "wait", never as "no". */
  showAutomatically: boolean | undefined;
  isLoading: boolean;
  isError: boolean;
  refetch: () => Promise<unknown>;
}

export function useAnnouncementFeed(): AnnouncementFeed {
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const { user } = useNetUser();
  const userId = user?.id ?? null;

  const query = useQuery<AnnouncementFeedDto, AnnouncementsError>({
    queryKey: announcementFeedQueryKey(userId),
    enabled: userId !== null,
    queryFn: async () => unwrap(await api.announcements.feed(webAnnouncementQuery())),
    staleTime: 60_000,
    // A deploy can land the endpoint a few seconds late; one retry, then "nothing to show".
    retry: 1,
    refetchOnWindowFocus: true,
  });

  const items = query.data?.items ?? EMPTY_ITEMS;
  const unreadItems = useMemo(() => items.filter((item) => !item.isRead), [items]);
  const { refetch } = query;
  return {
    items,
    unreadItems,
    unreadCount: query.isError ? 0 : (query.data?.unreadCount ?? 0),
    showAutomatically: query.data?.showAutomatically,
    isLoading: query.isLoading,
    isError: query.isError,
    refetch,
  };
}

/** Marks `ids` read (omit for everything the feed shows) and updates the cached feed and badge. */
export function useMarkAnnouncementsRead() {
  const queryClient = useQueryClient();
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const { user } = useNetUser();
  const key = announcementFeedQueryKey(user?.id);

  const mutation = useMutation({
    mutationFn: async (ids?: string[]) => unwrap(await api.announcements.markRead(webAnnouncementQuery(), ids)),
    onSuccess: (response, ids) => {
      queryClient.setQueryData<AnnouncementFeedDto>(key, (feed) =>
        feed ? applyRead(feed, ids, response.unreadCount) : feed,
      );
    },
  });

  const markRead = useCallback((ids?: string[]) => mutation.mutateAsync(ids), [mutation]);
  return { markRead, isPending: mutation.isPending };
}

/**
 * "Vis nyheder automatisk": the server-stored opt-out. It only hides the launch dialog; the sidebar
 * row, the page and the badge stay. Optimistic on the cached feed, rolled back on error.
 */
export function useProductNewsPreference() {
  const queryClient = useQueryClient();
  const api = useMesterplanApi() as unknown as MesterplanApi;
  const { user } = useNetUser();
  const key = announcementFeedQueryKey(user?.id);

  const mutation = useMutation({
    mutationFn: async (showAutomatically: boolean) => unwrap(await api.announcements.setPreferences(showAutomatically)),
    onMutate: async (showAutomatically) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<AnnouncementFeedDto>(key);
      queryClient.setQueryData<AnnouncementFeedDto>(key, (feed) => (feed ? { ...feed, showAutomatically } : feed));
      return { previous };
    },
    onError: (_error, _value, context) => {
      if (context?.previous) queryClient.setQueryData<AnnouncementFeedDto>(key, context.previous);
    },
  });

  // Same query key as the page: one request, shared cache.
  const { showAutomatically } = useAnnouncementFeed();

  const setShowAutomatically = useCallback(
    (value: boolean) => mutation.mutateAsync(value).then(() => undefined),
    [mutation],
  );

  return {
    /** `undefined` until the feed has loaded: render the switch disabled. */
    showAutomatically,
    setShowAutomatically,
    isSaving: mutation.isPending,
  };
}
